import { AlertController, IonicPage, ModalController } from 'ionic-angular';
import { Component, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

import { AddVehiclePage, AddVehicleParams } from '../add-vehicle/add-vehicle';
import { AddressProvider } from '../../providers/address/address';
import {
  CancelledOrderParams,
  CreateAndCompleteOrderParams,
  OpsOrderModel,
} from '../../models/order.ops';
import { IDType, VehicleProvider } from '../../providers/vehicle/vehicle';
import { InspectionFormService } from '../order-detail/components/order-detail-tasks-list/inspection-form.service';
import { LoadingAlertProvider } from '../../providers/loading-alert/loading-alert';
import { OpsProvider } from '../../providers/ops/ops';
import { OpsVehicleModel } from './../../models/vehicle.ops';
import { OrderCancelReason } from './../../models/order.ops';
import {
  OrderCompletePage,
  OrderCompletePageProps,
} from '../order-complete/order-complete';
import { ServiceModel } from '../../models/service';
import { ShiftPage } from '../shift/shift';
import { TruckModel } from '../../models/truck';
import { UserAddressModel } from '../../models/user-address';
import { UserModel } from '../../models/user';
import { UserProvider } from '../../providers/user/user';
import { VehicleAutocompletePage } from '../vehicle-autocomplete/vehicle-autocomplete';
import { VehicleMileagePage } from './mileage/mileage';

@IonicPage()
@Component({
  selector: 'page-fleet',
  templateUrl: 'fleet.html',
})
export class FleetPage implements OnDestroy {
  truck: TruckModel;
  accounts: UserModel[];
  selectedAccount: UserModel;
  selectedType: IDType;
  selectedVehicle: OpsVehicleModel;
  selectedLocation: UserAddressModel;
  numberOfGallons: number;
  vehicleParams: AddVehicleParams;
  isLoading = true;

  selectedService: ServiceModel;
  services: ServiceModel[];
  mileage?: number;

  private unsubscribe: Subject<void> = new Subject();

  vehicleIdTypes: IDType[] = [];

  constructor(
    private opsService: OpsProvider,
    private loadingAlert: LoadingAlertProvider,
    private modalCtrl: ModalController,
    private vehicleService: VehicleProvider,
    private addressService: AddressProvider,
    private inspectionForm: InspectionFormService,
    public userService: UserProvider,
    private alertCtlr: AlertController
  ) {
    this.loadData();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  // Data

  loadData() {
    this.opsService.loadSelectedFleetAccount();
    this.userService.getCurrentFillerShift();
    this.opsService.selectedFleetAccount$
      .takeUntil(this.unsubscribe)
      .subscribe(account => (this.selectedAccount = account));

    this.getFleetAccounts();
    this.getTruck();
    this.vehicleIdTypes = this.vehicleService.vehicleIdTypes;
  }

  getTruck() {
    this.opsService.loadSelectedTruck();
    this.opsService.selectedTruck$
      .takeUntil(this.unsubscribe)
      .subscribe((truck: TruckModel) => (this.truck = truck));
  }

  getFleetAccounts() {
    this.opsService
      .getFleetAccounts()
      .finally(() => (this.isLoading = false))
      .subscribe(
        accounts => {
          this.accounts = this.sortAccounts(accounts);
        },
        () => {
          this.loadingAlert.showToastAlert('Error loading fleet accounts');
        }
      );
  }

  saveOrder() {
    const params = this.createOrderParams();
    this.loadingAlert.showLoader();
    this.opsService
      .createAndCompleteOrder(params)
      .finally(() => this.loadingAlert.dismissLoader())
      .subscribe(
        (order: OpsOrderModel) => {
          this.didSaveOrder(order);
        },
        error => {
          this.loadingAlert.showToastAlert(
            typeof error === 'string' ? error : 'Error saving order'
          );
        }
      );
  }

  didSaveOrder(order: OpsOrderModel) {
    this.clearFormData();
    this.loadingAlert.showToastConfirmation(
      'Order saved! You can submit a new order'
    );
  }

  // Actions

  didSelectLocation() {
    this.addressService
      .getServicesForAddress(this.selectedLocation.uid)
      .takeUntil(this.unsubscribe)
      .subscribe(services => (this.services = services));
  }

  didSelectAccount(account: UserModel) {
    this.opsService.setSelectedFleetAccount(account);
  }

  didTapVehicle() {
    let modal = this.modalCtrl.create(VehicleAutocompletePage, {
      account: this.selectedAccount,
      type: this.selectedType.type,
      onNewVehicle: (type, query) => this.didTapNewVehicle(type, query),
    });
    modal.onDidDismiss(vehicle => {
      this.vehicleParams = null;
      this.selectedVehicle = vehicle ? vehicle : this.selectedVehicle;
    });
    modal.present();
  }

  didTapNewVehicle(type, query: ConstrainDOMString) {
    let params = {};
    params[type] = query;
    let modal = this.modalCtrl.create(AddVehiclePage, params);
    modal.onDidDismiss((params: AddVehicleParams) => {
      this.selectedVehicle = null;
      this.vehicleParams = params;
    });
    modal.present();
  }

  didTapSubmit() {
    if (this.selectedService.inspectionFormUrl) {
      this.didTapInspectionForm();
    } else {
      const props: OrderCompletePageProps = {
        truck: this.truck,
        isClockedIn: this.opsService.currentFillerShift !== null,
        isFleet: true,
        service: this.selectedService,
        params: this.createOrderParams(),
      };
      const modal = this.modalCtrl.create(OrderCompletePage, {
        props,
        didCompleteOrder: (order: OpsOrderModel) => this.didSaveOrder(order),
      });
      modal.present();
    }
  }

  didTapCancel() {
    let alert = this.alertCtlr.create({
      title: 'Please choose a relevant cancellation reason for this vehicle:',
      inputs: [
        {
          type: 'radio',
          label: 'Owner Missing',
          value: OrderCancelReason.OwnerMissing,
        },
      ],
      buttons: [
        {
          text: 'Log Cancelled Order',
          handler: data => {
            this.createCancelledOrder(data);
          },
        },
        {
          text: 'Dismiss',
        },
      ],
    });
    alert.present();
  }

  createCancelledOrder(cancellationReason: OrderCancelReason) {
    const params = this.prepareParamsForCancelledOrder(cancellationReason);

    this.loadingAlert.showLoader();
    this.opsService
      .createCancelledOrder(params)
      .finally(() => this.loadingAlert.dismissLoader())
      .subscribe(
        () => {
          this.clearFormData();
          this.loadingAlert.showToastConfirmation(
            'Created a cancelled order! You can submit a new order'
          );
        },
        error => {
          this.loadingAlert.showToastAlert(
            typeof error === 'string'
              ? error
              : 'Error creating a cancelled order'
          );
        }
      );
  }

  didTapInspectionForm() {
    const params = this.createInspectionFormParams();
    const url = encodeURI(
      `${this.selectedService.inspectionFormUrl}?${params}`
    );
    this.inspectionForm.present(url).then(
      () => this.saveOrder(),
      error => {
        this.loadingAlert.presentAlert(
          'Error completing inspection form',
          error
        );
      }
    );
  }

  private createInspectionFormParams() {
    const toQueryString = params =>
      Object.keys(params)
        .filter(key => params[key] != null)
        .map(key => key + '=' + params[key])
        .join('&');
    const customerName = `${this.selectedAccount.firstName} ${this.selectedAccount.lastName}`.trim();
    const params: any = {
      customerName,
      email: this.selectedAccount.email,
      customerPhone: this.selectedAccount.phone,
    };
    if (this.vehicleParams) {
      params.licensePlate = this.vehicleParams.license;
      params.vehicleMake = this.vehicleParams.vehicle_make;
      params.vehicleModel = this.vehicleParams.vehicle_model;
    } else if (this.selectedVehicle) {
      params.licensePlate = this.selectedVehicle.license;
      params.vehicleMake = this.selectedVehicle.vehicleMake.make;
      params.vehicleModel = this.selectedVehicle.vehicleModel.model;
    }
    return toQueryString(params);
  }

  private prepareParamsForCancelledOrder(
    cancellationReason: string
  ): CancelledOrderParams {
    let params: CancelledOrderParams = {
      account_uid: this.selectedAccount.uid,
      user_address_uid: this.selectedLocation.uid,
      service_uid: this.selectedService.uid,
      cancellation_reason: cancellationReason,
    };

    if (this.vehicleParams) {
      params.vin = this.vehicleParams.vin || '';
      params.license = this.vehicleParams.license || '';
      params.serial_number = this.vehicleParams.serial_number || '';
      params.vehicle_model_uid = this.vehicleParams.vehicle_model_uid;
    } else if (this.selectedVehicle) {
      params.vehicle_uid = this.selectedVehicle.uid;
    }
    return params;
  }

  private createOrderParams(): CreateAndCompleteOrderParams {
    const params: CreateAndCompleteOrderParams = {
      account_uid: this.selectedAccount.uid,
      user_address_uid: this.selectedLocation.uid,
      service_uid: this.selectedService.uid,
    };

    if (this.vehicleParams) {
      params.vin = this.vehicleParams.vin || '';
      params.license = this.vehicleParams.license || '';
      params.serial_number = this.vehicleParams.serial_number || '';
      params.vehicle_model_uid = this.vehicleParams.vehicle_model_uid;
    }
    if (this.selectedVehicle) {
      params.vehicle_uid = this.selectedVehicle.uid;
    }
    return params;
  }

  private clearFormData() {
    this.numberOfGallons = 0;
    this.selectedVehicle = null;
    this.vehicleParams = null;
    this.selectedType = null;
    this.mileage = null;
  }

  didTapActionButton() {
    const modal = this.modalCtrl.create(ShiftPage);
    modal.present();
  }

  didTapAddMiles() {
    const modal = this.modalCtrl.create(VehicleMileagePage, {
      vehicle: this.selectedVehicle,
    });
    modal.onDidDismiss(mileage => (this.mileage = mileage));
    modal.present();
  }

  // Helpers

  validates() {
    return (
      this.selectedAccount &&
      this.selectedLocation &&
      this.selectedType &&
      (this.selectedVehicle || this.vehicleParams)
    );
  }

  validateLogCancelled() {
    let hasVehicle = this.selectedVehicle || this.vehicleParams;

    return (
      this.selectedAccount &&
      this.selectedLocation &&
      this.selectedType &&
      hasVehicle &&
      this.selectedService
    );
  }

  get vehiclePlaceholder() {
    if (this.selectedVehicle) {
      if (this.selectedType.type == 'license') {
        return 'Existing: ' + this.selectedVehicle.license;
      }
      return 'Existing: ' + this.selectedVehicle[this.selectedType.type];
    } else if (this.vehicleParams) {
      return 'New: ' + this.vehicleParams[this.selectedType.type];
    } else if (this.selectedType) {
      return 'Enter ' + this.selectedType.shortLabel;
    }
    return '';
  }

  private sortAccounts(accounts: UserModel[]) {
    return accounts.sort((a, b) => (a.firstName > b.firstName ? 1 : -1));
  }
}



// WEBPACK FOOTER //
// ./src/pages/fleet/fleet.ts